import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import ReportQuestionModal from '../components/common/ReportQuestionModal';
import UpgradeModal from '../components/common/UpgradeModal';
import { VideoModal, WatchVideoButton } from '../components/common/VideoModal';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { 
  ChevronLeft, ChevronDown, ChevronRight, Bookmark, BookmarkCheck,
  Loader2, FileText, RefreshCw, Calendar, Info, Circle, CheckCircle2, RotateCcw, Flag, ZoomIn, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WrittenPaperQuestionsPage = () => {
  const navigate = useNavigate();
  const { paperId } = useParams();
  const { user } = useAuth();
  const { preferences } = usePreferences();
  
  const [paper, setPaper] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [completedIds, setCompletedIds] = useState(new Set());
  
  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  
  // Image lightbox state
  const [enlargedImage, setEnlargedImage] = useState(null);
  
  // Filters - simplified: completed, incomplete, bookmarked
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'completed', 'incomplete', 'bookmarked'
  
  // Expanded answers
  const [expandedAnswers, setExpandedAnswers] = useState(new Set());
  
  // Report modal state
  const [reportModal, setReportModal] = useState({
    open: false,
    questionId: null,
    questionText: ''
  });

  // Explainer video modal state
  const [videoModal, setVideoModal] = useState({ open: false, url: null });

  // Helper function to get proper image URL
  const getImageUrl = (img) => {
    if (!img || !img.url) return '';
    if (img.url.startsWith('http')) return img.url;
    return `${BACKEND_URL}${img.url}`;
  };

  useEffect(() => {
    fetchData();
    checkSubscription();
    if (user) {
      fetchBookmarks();
      fetchCompleted();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paperId, user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/written/papers/${paperId}/public`);
      setPaper(response.data.paper);
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Failed to fetch paper:', error);
      toast.error('Failed to load paper');
      navigate('/written-bank');
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    // Only check subscription if authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      // Guest users default to free status (answers locked)
      setSubscriptionStatus({ subscription_status: 'free', is_active: false });
      return;
    }
    
    try {
      const response = await axios.get(`${API}/user/subscription`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to check subscription:', error);
      // If API fails, default to free
      setSubscriptionStatus({ subscription_status: 'free', is_active: false });
    }
  };

  const fetchBookmarks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/written/bookmarks`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setBookmarkedIds(new Set(response.data.bookmarked_ids || []));
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
    }
  };

  const fetchCompleted = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/written/completed`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCompletedIds(new Set(response.data.completed_ids || []));
    } catch (error) {
      console.error('Failed to fetch completed:', error);
    }
  };

  const toggleBookmark = async (questionId) => {
    if (!user) {
      toast.error('Please login to bookmark questions');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/written/bookmarks/${questionId}`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setBookmarkedIds(prev => {
        const newSet = new Set(prev);
        if (response.data.bookmarked) {
          newSet.add(questionId);
          toast.success('Question bookmarked');
        } else {
          newSet.delete(questionId);
          toast.success('Bookmark removed');
        }
        return newSet;
      });
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      toast.error('Failed to update bookmark');
    }
  };

  const toggleCompleted = async (questionId) => {
    if (!user) {
      toast.error('Please login to track progress');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/written/completed/${questionId}`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCompletedIds(prev => {
        const newSet = new Set(prev);
        if (response.data.completed) {
          newSet.add(questionId);
        } else {
          newSet.delete(questionId);
        }
        return newSet;
      });
    } catch (error) {
      console.error('Failed to toggle completed:', error);
      toast.error('Failed to update progress');
    }
  };

  const toggleAnswer = async (questionId) => {
    // Check if user has paid access for the current grade
    const subscribedGrade = subscriptionStatus?.subscribed_grade;
    const userRole = subscriptionStatus?.user_role;
    const currentGrade = paper?.grade || preferences?.grade;
    
    // Admins/sub-admins have access to all grades
    const isAdmin = userRole === 'admin' || userRole === 'sub_admin';
    
    // Check if user has access: either admin, or subscribed to 'all', or subscribed to current grade
    const hasGradeAccess = isAdmin || subscribedGrade === 'all' || subscribedGrade === currentGrade;
    const hasAccess = isAdmin || (subscriptionStatus?.subscription_status === 'paid' && subscriptionStatus?.is_active && hasGradeAccess);
    
    if (!hasAccess) {
      // User doesn't have access - show upgrade modal
      setShowUpgrade(true);
      return;
    }
    
    // User has access - toggle answer
    const isExpanding = !expandedAnswers.has(questionId);
    
    setExpandedAnswers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
    
    // Track as "viewed" when expanding answer (for progress tracker)
    if (isExpanding && user) {
      try {
        await axios.post(`${API}/progress/action`, {
          question_id: questionId,
          action: 'view',
          question_type: 'written'
        });
      } catch (error) {
        // Silent fail - don't interrupt user experience
        console.error('Failed to track view:', error);
      }
    }
  };

  const handleResetCompleted = async () => {
    if (!user) return;
    
    if (!window.confirm('Are you sure you want to reset all completed progress for this paper? This will mark all questions as not completed.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/written/completed/reset`, 
        { paper_id: paperId },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      toast.success(response.data.message || 'Progress reset successfully');
      
      // Reset local completed state for this paper's questions
      const paperQuestionIds = questions.map(q => q.id);
      setCompletedIds(prev => {
        const newSet = new Set(prev);
        paperQuestionIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } catch (error) {
      console.error('Failed to reset progress:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset progress');
    }
  };

  // Calculate counts for filter buttons
  const filterCounts = useMemo(() => {
    const completedCount = questions.filter(q => completedIds.has(q.id)).length;
    const incompleteCount = questions.length - completedCount;
    const bookmarkedCount = questions.filter(q => bookmarkedIds.has(q.id)).length;
    return { completedCount, incompleteCount, bookmarkedCount };
  }, [questions, completedIds, bookmarkedIds]);

  // Filter questions based on active filter
  const filteredQuestions = useMemo(() => {
    return questions.filter(q => {
      if (activeFilter === 'completed') return completedIds.has(q.id);
      if (activeFilter === 'incomplete') return !completedIds.has(q.id);
      if (activeFilter === 'bookmarked') return bookmarkedIds.has(q.id);
      return true; // 'all'
    });
  }, [questions, activeFilter, completedIds, bookmarkedIds]);

  // Toggle filter - click same filter again to reset to 'all'
  const handleFilterClick = (filter) => {
    setActiveFilter(prev => prev === filter ? 'all' : filter);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#14B8A6]" />
        </div>
      </Layout>
    );
  }

  if (!paper) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Paper not found</p>
          <Button onClick={() => navigate('/written-bank')} className="mt-4">
            Back to Written Bank
          </Button>
        </div>
      </Layout>
    );
  }

  const getPaperTitle = () => {
    if (paper.paper_number) {
      return `${paper.exam_month} - Paper ${paper.paper_number}`;
    }
    return paper.exam_month;
  };

  return (
    <Layout>
      <div className="page-transition">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/written-bank')}
            className="mb-2"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Papers
          </Button>
          
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant="static" className="bg-[#14B8A6] text-white">{paper.grade}</Badge>
                <Badge variant="outline" className="max-w-full whitespace-normal break-words text-left">{paper.function_display}</Badge>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 break-words">{getPaperTitle()}</h1>
              <p className="text-gray-600 break-words">{paper.function_name}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge variant="static" className="bg-gray-100 text-gray-700 text-sm px-3 py-1">
                {filteredQuestions.length} of {questions.length} questions
              </Badge>
              {user && (
                <Badge variant="static" className="bg-green-100 text-green-700 text-sm px-3 py-1">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {filterCounts.completedCount}/{questions.length} completed
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Filter Buttons Row */}
        {user && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <Button
              variant={activeFilter === 'completed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterClick('completed')}
              className={activeFilter === 'completed' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Completed ({filterCounts.completedCount})
            </Button>
            <Button
              variant={activeFilter === 'incomplete' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterClick('incomplete')}
              className={activeFilter === 'incomplete' ? 'bg-orange-500 hover:bg-orange-600' : ''}
            >
              <Circle className="w-4 h-4 mr-1" />
              Incomplete ({filterCounts.incompleteCount})
            </Button>
            <Button
              variant={activeFilter === 'bookmarked' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterClick('bookmarked')}
              className={activeFilter === 'bookmarked' ? 'bg-[#EC4899] hover:bg-[#EC4899]/90' : ''}
            >
              <BookmarkCheck className="w-4 h-4 mr-1" />
              Bookmarked ({filterCounts.bookmarkedCount})
            </Button>
            
            {/* Reset Button - only show if there are completed questions */}
            {filterCounts.completedCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetCompleted}
                className="text-gray-500 hover:text-red-600 ml-auto"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
            )}
          </div>
        )}

        {/* Questions List */}
        <div className="space-y-4">
          {filteredQuestions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No questions match your filter</p>
                {activeFilter !== 'all' && (
                  <Button 
                    variant="link" 
                    onClick={() => setActiveFilter('all')}
                    className="mt-2 text-[#EC4899]"
                  >
                    Show all questions
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredQuestions.map((question, index) => {
              const isCompleted = completedIds.has(question.id);
              // Calculate actual index in full list for numbering
              const actualIndex = questions.findIndex(q => q.id === question.id);
              return (
                <Card key={question.id} className={`overflow-hidden ${isCompleted ? 'bg-gray-50/50' : ''}`}>
                  <CardContent className="p-0">
                    {/* Question Header */}
                    <div className={`p-4 border-b ${isCompleted ? 'bg-gray-100/50' : 'bg-gray-50'}`}>
                      <div className="flex items-start gap-3">
                        {/* Completion Circle */}
                        <button
                          onClick={() => toggleCompleted(question.id)}
                          className={`mt-1 flex-shrink-0 transition-colors ${
                            user 
                              ? 'cursor-pointer hover:text-green-600' 
                              : 'cursor-not-allowed text-gray-300'
                          }`}
                          title={user ? (isCompleted ? 'Mark as incomplete' : 'Mark as completed') : 'Login to track progress'}
                        >
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : (
                            <Circle className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className={`font-bold text-[#EC4899] ${isCompleted ? 'line-through opacity-60' : ''}`}>
                                  Q{actualIndex + 1}.
                                </span>
                                {question.marks && (
                                  <Badge variant="outline" className={`text-xs ${isCompleted ? 'opacity-60' : ''}`}>
                                    {question.marks} marks
                                  </Badge>
                                )}
                                {question.topic_name && (
                                  <Badge variant="static" className={`bg-purple-100 text-purple-800 text-xs max-w-full whitespace-normal break-words text-left ${isCompleted ? 'opacity-60' : ''}`}>
                                    {question.topic_name}
                                  </Badge>
                                )}
                                {/* Asked count with HoverCard for appearances */}
                                {question.asked_count > 1 && question.appearances && question.appearances.length > 1 && (
                                  <HoverCard openDelay={100} closeDelay={100}>
                                    <HoverCardTrigger asChild>
                                      <button 
                                        type="button"
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border cursor-help bg-blue-50 text-blue-700 border-blue-300 ${isCompleted ? 'opacity-60' : ''}`}
                                      >
                                        <RefreshCw className="w-3 h-3" />
                                        Asked {question.asked_count}x
                                        <Info className="w-3 h-3 ml-0.5" />
                                      </button>
                                    </HoverCardTrigger>
                                    <HoverCardContent 
                                      side="top" 
                                      className="w-auto p-3 bg-gray-900 text-white border-gray-700"
                                    >
                                      <p className="font-medium mb-2 text-sm">Asked in:</p>
                                      <ul className="text-xs space-y-1">
                                        {question.appearances.map((app, i) => (
                                          <li key={i} className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3 text-gray-400" />
                                            {app.exam_month}{app.paper_number ? ` (Paper ${app.paper_number})` : ''}
                                          </li>
                                        ))}
                                      </ul>
                                    </HoverCardContent>
                                  </HoverCard>
                                )}
                              </div>
                              <p className={`text-gray-800 whitespace-pre-wrap ${isCompleted ? 'line-through opacity-60' : ''}`}>
                                {question.question_text}
                              </p>
                              
                              {/* Question Images */}
                              {question.question_images?.length > 0 && (
                                <div className="flex gap-3 mt-3 flex-wrap">
                                  {question.question_images.map((img, i) => (
                                    <div 
                                      key={img.id || i}
                                      className="relative group cursor-pointer rounded-lg overflow-hidden border bg-white"
                                      onClick={() => setEnlargedImage({ url: getImageUrl(img), caption: img.caption })}
                                    >
                                      <img 
                                        src={getImageUrl(img)} 
                                        alt={img.caption || `Question image ${i+1}`}
                                        className="max-h-48 w-auto object-contain transition-opacity group-hover:opacity-90"
                                      />
                                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                                        <ZoomIn className="w-8 h-8 text-white drop-shadow-lg" />
                                      </div>
                                      {img.caption && (
                                        <p className="text-xs text-gray-600 p-2 bg-gray-50 border-t">
                                          {img.caption}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex items-center gap-1">
                              {/* Flag Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setReportModal({
                                  open: true,
                                  questionId: question.id,
                                  questionText: question.question_text
                                })}
                                className="text-gray-400 hover:text-orange-500"
                                title="Report this question"
                              >
                                <Flag className="w-4 h-4" />
                              </Button>
                              
                              {/* Bookmark Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleBookmark(question.id)}
                                className={bookmarkedIds.has(question.id) ? 'text-[#EC4899]' : 'text-gray-400'}
                              >
                                {bookmarkedIds.has(question.id) ? (
                                  <BookmarkCheck className="w-5 h-5" />
                                ) : (
                                  <Bookmark className="w-5 h-5" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Answer Section */}
                    {question.has_answer ? (
                      <Collapsible 
                        open={expandedAnswers.has(question.id)}
                        onOpenChange={() => toggleAnswer(question.id)}
                      >
                        <CollapsibleTrigger className="w-full">
                          <div className="px-4 py-3 flex items-center gap-2 text-sm text-[#EC4899] hover:bg-gray-50 transition-colors">
                            {expandedAnswers.has(question.id) ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                            {expandedAnswers.has(question.id) ? 'Hide Answer' : 'Show Answer'}
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="px-4 pb-4">
                            <div className="p-4 bg-green-50 rounded-lg">
                              <p className="text-sm font-medium text-green-800 mb-2 flex items-center gap-2">
                                Answer:
                                {question.video_url && (
                                  <WatchVideoButton
                                    videoUrl={question.video_url}
                                    onClick={() => setVideoModal({ open: true, url: question.video_url })}
                                  />
                                )}
                              </p>
                              <p className="text-green-700 whitespace-pre-wrap">{question.answer_text}</p>
                              
                              {/* Answer Images */}
                              {question.answer_images?.length > 0 && (
                                <div className="flex gap-3 mt-3 flex-wrap">
                                  {question.answer_images.map((img, i) => (
                                    <div 
                                      key={img.id || i}
                                      className="relative group cursor-pointer rounded-lg overflow-hidden border bg-white"
                                      onClick={() => setEnlargedImage({ url: getImageUrl(img), caption: img.caption })}
                                    >
                                      <img 
                                        src={getImageUrl(img)} 
                                        alt={img.caption || `Answer image ${i+1}`}
                                        className="max-h-48 w-auto object-contain transition-opacity group-hover:opacity-90"
                                      />
                                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                                        <ZoomIn className="w-8 h-8 text-white drop-shadow-lg" />
                                      </div>
                                      {img.caption && (
                                        <p className="text-xs text-gray-600 p-2 bg-gray-50 border-t">
                                          {img.caption}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500 italic border-t border-dashed">
                        Answer coming soon
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
        
        {/* Image Lightbox Modal */}
        <Dialog open={!!enlargedImage} onOpenChange={() => setEnlargedImage(null)}>
          <DialogContent className="max-w-4xl w-full p-0 bg-black/95 border-none">
            <button
              onClick={() => setEnlargedImage(null)}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            {enlargedImage && (
              <div className="flex flex-col items-center justify-center p-4">
                <img
                  src={enlargedImage.url}
                  alt={enlargedImage.caption || 'Enlarged image'}
                  className="max-w-full max-h-[80vh] object-contain"
                />
                {enlargedImage.caption && (
                  <p className="text-white text-center mt-4 text-sm">{enlargedImage.caption}</p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Report Question Modal */}
        <ReportQuestionModal
          open={reportModal.open}
          onClose={() => setReportModal({ open: false, questionId: null, questionText: '' })}
          questionId={reportModal.questionId}
          questionText={reportModal.questionText}
          questionType="written"
        />
        
        {/* Upgrade Modal */}
        <UpgradeModal 
          isOpen={showUpgrade} 
          onClose={() => setShowUpgrade(false)} 
          feature="answers"
          subscribedGrade={subscriptionStatus?.subscribed_grade}
          targetGrade={paper?.grade || preferences?.grade}
        />

        {/* Explainer Video Modal — YouTube / Dadan iframe */}
        {videoModal.open && (
          <VideoModal
            videoUrl={videoModal.url}
            onClose={() => setVideoModal({ open: false, url: null })}
          />
        )}
      </div>
    </Layout>
  );
};

export default WrittenPaperQuestionsPage;
